import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
} from 'react'
import useConversation from '../hooks/useConversation'
import useLoraModels from '../hooks/useLoraModels'
import {
  fileToBase64,
  useAuthenticatedFetch,
  type ConversationTurn,
  type Generation,
  type UploadedImage,
} from '../lib/api'
import { IMAGE_MODELS } from '../lib/models'

const MAX_REFERENCE_IMAGES = 3
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

interface PendingAttachment {
  id: string
  // Local preview while uploading; replaced with R2 URL on success
  previewUrl: string
  // Set once upload completes
  uploaded: UploadedImage | null
  status: 'uploading' | 'ready' | 'error'
  error?: string
}

interface ChatPanelProps {
  open: boolean
  onClose: () => void
  onOpenGeneration: (generation: Generation) => void
  onReusePrompt: (prompt: string, model: string) => void
}

// Minimal Generation-shaped adapter from a conversation turn
function turnToGeneration(
  turn: ConversationTurn,
  modelId: string,
): Generation | null {
  if (!turn.r2_url || !turn.generation_id) return null
  return {
    id: turn.generation_id,
    user_id: '',
    model: modelId,
    prompt: turn.refined_prompt ?? '',
    type: 'image',
    r2_url: turn.r2_url,
    nsfw: false,
    status: 'complete',
    created_at: turn.created_at,
  }
}

export default function ChatPanel({
  open,
  onClose,
  onOpenGeneration,
  onReusePrompt,
}: ChatPanelProps) {
  const {
    conversation,
    turns,
    streamingText,
    streamingStage,
    conversations,
    isSending,
    isLoading,
    error,
    startNewConversation,
    openConversation,
    resetConversation,
    sendMessage,
    clearError,
    deleteConversation,
  } = useConversation()

  const authenticatedFetch = useAuthenticatedFetch()
  const loraModels = useLoraModels()
  const [input, setInput] = useState('')
  const [selectedModel, setSelectedModel] = useState('flux-schnell')
  const [showHistory, setShowHistory] = useState(false)
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Revoke any object URLs when this component unmounts to avoid leaks.
  useEffect(() => {
    return () => {
      attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files).filter((f) =>
        ACCEPTED_IMAGE_TYPES.includes(f.type),
      )
      if (list.length === 0) return

      const remainingSlots = MAX_REFERENCE_IMAGES - attachments.length
      const accepted = list.slice(0, Math.max(0, remainingSlots))

      const pending: PendingAttachment[] = accepted.map((file) => ({
        id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        previewUrl: URL.createObjectURL(file),
        uploaded: null,
        status: 'uploading',
      }))
      setAttachments((prev) => [...prev, ...pending])

      await Promise.all(
        pending.map(async (att, i) => {
          const file = accepted[i]
          try {
            const data_base64 = await fileToBase64(file)
            const res = await authenticatedFetch('/api/uploads', {
              method: 'POST',
              body: JSON.stringify({
                content_type: file.type,
                data_base64,
              }),
            })
            const { data } = (await res.json()) as { data: UploadedImage }
            setAttachments((prev) =>
              prev.map((p) =>
                p.id === att.id ? { ...p, uploaded: data, status: 'ready' } : p,
              ),
            )
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Upload failed'
            setAttachments((prev) =>
              prev.map((p) =>
                p.id === att.id ? { ...p, status: 'error', error: msg } : p,
              ),
            )
          }
        }),
      )
    },
    [attachments.length, authenticatedFetch],
  )

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((p) => p.id === id)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((p) => p.id !== id)
    })
  }, [])

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((a) => URL.revokeObjectURL(a.previewUrl))
      return []
    })
  }, [])

  // Auto-scroll to bottom on new turns
  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [turns.length, open, isSending])

  const anyUploading = attachments.some((a) => a.status === 'uploading')
  const readyImageUrls = attachments
    .filter((a) => a.status === 'ready' && a.uploaded)
    .map((a) => a.uploaded!.url)

  const handleSend = useCallback(async () => {
    const trimmed = input.trim()
    if (!trimmed || isSending || anyUploading) return

    setInput('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    const imageUrls = [...readyImageUrls]
    clearAttachments()

    if (!conversation) {
      await startNewConversation(selectedModel, trimmed, imageUrls)
    } else {
      await sendMessage(trimmed, imageUrls)
    }
  }, [
    input,
    isSending,
    anyUploading,
    readyImageUrls,
    clearAttachments,
    conversation,
    selectedModel,
    startNewConversation,
    sendMessage,
  ])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const handleNewChat = useCallback(() => {
    resetConversation()
    setInput('')
  }, [resetConversation])

  // CommandPalette fires this event so "Start a new chat" works from any
  // page without knowing about ChatPanel's local state.
  useEffect(() => {
    const onReset = () => resetConversation()
    window.addEventListener('fv:chat-reset', onReset)
    return () => window.removeEventListener('fv:chat-reset', onReset)
  }, [resetConversation])

  const title = conversation?.title ?? 'New conversation'

  return (
    <>
      {/* Backdrop on mobile */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity md:hidden ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault()
            setIsDragOver(true)
          }
        }}
        onDragLeave={(e) => {
          // Only clear if leaving the panel itself
          if (e.currentTarget.contains(e.relatedTarget as Node)) return
          setIsDragOver(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
          if (e.dataTransfer.files.length > 0) {
            uploadFiles(e.dataTransfer.files)
          }
        }}
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[420px] bg-bg-secondary border-l border-border flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${isDragOver ? 'ring-2 ring-neon ring-inset' : ''}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-8 h-8 rounded-lg bg-neon/10 border border-neon/30 flex items-center justify-center shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#39FF14"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-text-primary truncate">
                {title}
              </h3>
              <p className="text-[11px] text-text-muted truncate italic">
                resident art director · drink in hand
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
              title="History"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </button>
            <button
              onClick={handleNewChat}
              className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
              title="New chat"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
              title="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* History drawer */}
        {showHistory && (
          <div className="border-b border-border bg-bg-card max-h-64 overflow-y-auto">
            {conversations.length === 0 ? (
              <p className="p-4 text-xs text-text-muted text-center">No past conversations</p>
            ) : (
              conversations.map((c) => (
                <div
                  key={c.id}
                  className={`group flex items-center gap-2 px-4 py-2.5 hover:bg-bg-elevated cursor-pointer ${
                    c.id === conversation?.id ? 'bg-bg-elevated border-l-2 border-neon' : ''
                  }`}
                  onClick={() => {
                    openConversation(c.id)
                    setShowHistory(false)
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-text-primary truncate">{c.title}</p>
                    <p className="text-[10px] text-text-muted">
                      {new Date(c.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteConversation(c.id)
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-error transition-all"
                    title="Delete"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {isLoading && turns.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 rounded-full border-2 border-neon border-t-transparent animate-spin" />
            </div>
          )}

          {!isLoading && turns.length === 0 && (
            <div className="text-center py-8 space-y-4 max-w-sm mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-neon/5 border border-neon/20 flex items-center justify-center mx-auto">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#39FF14"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                </svg>
              </div>
              <div className="space-y-1.5">
                <h4 className="brand-wordmark text-xl text-text-primary">
                  Sit, darling.
                </h4>
                <p className="text-xs text-text-secondary leading-relaxed italic">
                  Tell me what you want. I will draft something with taste,
                  generate it, and you will tell me what to change. We don't
                  do prompt engineering in this house — we have a{' '}
                  <span className="text-neon not-italic">conversation</span>.
                </p>
              </div>
              <div className="space-y-1.5 text-left">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
                  / try one of these
                </p>
                {[
                  'a cyberpunk samurai at dusk',
                  'a moody landscape at golden hour, fog rolling in',
                  'a retro anime cat astronaut, very serious',
                ].map((example) => (
                  <button
                    key={example}
                    onClick={() => setInput(example)}
                    className="w-full text-left text-xs px-3 py-2 rounded-lg bg-bg-card hover:bg-bg-elevated border border-border hover:border-neon italic text-text-secondary hover:text-text-primary transition-all cursor-pointer"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}

          {turns.map((turn) => (
            <ChatMessage
              key={turn.id}
              turn={turn}
              conversationModel={conversation?.model ?? 'flux-schnell'}
              onOpenImage={(gen) => onOpenGeneration(gen)}
              onReuse={(prompt) =>
                onReusePrompt(prompt, conversation?.model ?? 'flux-schnell')
              }
            />
          ))}

          {isSending && (
            <div className="flex items-start gap-2.5 animate-in fade-in">
              <div className="w-7 h-7 rounded-lg bg-neon/10 border border-neon/30 flex items-center justify-center shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#39FF14"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-sm bg-bg-card border border-border min-h-[2.5rem]">
                  {streamingText ? (
                    <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
                      {streamingText}
                      <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-neon align-middle animate-pulse" />
                    </p>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" />
                      <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse [animation-delay:0.2s]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse [animation-delay:0.4s]" />
                    </div>
                  )}
                </div>
                {streamingStage === 'generating' && (
                  <div className="rounded-xl border border-border bg-bg-card aspect-square relative overflow-hidden">
                    <div className="scan-beam" />
                    <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neon/80">
                        Developing
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {error && (
          <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-error/10 border border-error/30 text-error text-xs flex items-start gap-2">
            <span className="flex-1">{error}</span>
            <button
              onClick={clearError}
              className="text-error/70 hover:text-error"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Input */}
        <div className="p-3 border-t border-border bg-bg-secondary/80 backdrop-blur-sm shrink-0 space-y-2">
          {!conversation && (
            <div className="flex items-center gap-1.5 px-1 overflow-x-auto">
              <span className="text-[10px] text-text-muted shrink-0">Model:</span>
              {[...IMAGE_MODELS, ...loraModels].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelectedModel(m.id)}
                  className={`text-[10px] px-2 py-1 rounded-md border transition-colors shrink-0 cursor-pointer ${
                    selectedModel === m.id
                      ? 'border-neon bg-neon/10 text-neon'
                      : 'border-border text-text-secondary hover:border-border-hover'
                  }`}
                >
                  {m.name}
                </button>
              ))}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="flex items-center gap-2 px-1 overflow-x-auto">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className="relative shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-border bg-bg-card group"
                >
                  <img
                    src={att.previewUrl}
                    alt="Reference"
                    className="w-full h-full object-cover"
                  />
                  {att.status === 'uploading' && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full border-2 border-neon border-t-transparent animate-spin" />
                    </div>
                  )}
                  {att.status === 'error' && (
                    <div
                      className="absolute inset-0 bg-error/60 flex items-center justify-center"
                      title={att.error}
                    >
                      <span className="text-[10px] text-white font-bold">!</span>
                    </div>
                  )}
                  <button
                    onClick={() => removeAttachment(att.id)}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 hover:bg-black text-white text-[10px] leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              {attachments.length < MAX_REFERENCE_IMAGES && (
                <p className="text-[10px] text-text-muted shrink-0">
                  {MAX_REFERENCE_IMAGES - attachments.length} more allowed
                </p>
              )}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) uploadFiles(e.target.files)
              e.target.value = ''
            }}
          />
          <div className="flex items-end gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending || attachments.length >= MAX_REFERENCE_IMAGES}
              className="w-10 h-10 rounded-xl bg-bg-card border border-border hover:border-neon hover:text-neon text-text-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center shrink-0"
              title="Attach reference images"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                const el = e.target
                el.style.height = 'auto'
                el.style.height = `${Math.min(el.scrollHeight, 120)}px`
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                conversation
                  ? 'tell me what to change, darling…'
                  : 'tell me what you want to make…'
              }
              rows={1}
              disabled={isSending}
              className="input-glow flex-1 resize-none bg-bg-card border border-border rounded-xl px-3 py-2.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-neon transition-all disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={isSending || anyUploading || !input.trim()}
              className="w-10 h-10 rounded-xl bg-neon text-bg-primary hover:bg-neon-dim transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center shrink-0"
            >
              {isSending ? (
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-[10px] text-text-muted text-center">
            Shift + Enter for new line
          </p>
        </div>
      </aside>
    </>
  )
}

// ─── ChatMessage subcomponent ────────────────────────────────────────────────

interface ChatMessageProps {
  turn: ConversationTurn
  conversationModel: string
  onOpenImage: (generation: Generation) => void
  onReuse: (prompt: string) => void
}

function ChatMessage({
  turn,
  conversationModel,
  onOpenImage,
  onReuse,
}: ChatMessageProps) {
  const isUser = turn.role === 'user'
  const generation = turnToGeneration(turn, conversationModel)

  if (isUser) {
    let referenceUrls: string[] = []
    if (turn.image_urls) {
      try {
        const parsed = JSON.parse(turn.image_urls)
        if (Array.isArray(parsed)) referenceUrls = parsed.filter((u) => typeof u === 'string')
      } catch {
        // ignore malformed JSON
      }
    }
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] flex flex-col items-end gap-1.5">
          {referenceUrls.length > 0 && (
            <div className="flex flex-wrap justify-end gap-1.5">
              {referenceUrls.map((url) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-20 h-20 rounded-xl overflow-hidden border border-neon/30 bg-bg-card hover:border-neon transition-colors"
                >
                  <img
                    src={url}
                    alt="Reference"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          )}
          {turn.content && (
            <div className="px-3.5 py-2.5 rounded-2xl rounded-tr-sm bg-neon/10 border border-neon/20 text-sm text-text-primary">
              {turn.content}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Assistant message — may or may not have image
  return (
    <div className="flex items-start gap-2.5 animate-in fade-in">
      <div className="w-7 h-7 rounded-lg bg-neon/10 border border-neon/30 flex items-center justify-center shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#39FF14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-sm bg-bg-card border border-border">
          <p className="text-sm text-text-primary leading-relaxed whitespace-pre-wrap">
            {turn.content}
          </p>
        </div>

        {generation && (
          <div className="rounded-xl overflow-hidden border border-border bg-bg-card group">
            <button
              onClick={() => onOpenImage(generation)}
              className="block w-full aspect-square bg-black cursor-pointer"
            >
              <img
                src={generation.r2_url!}
                alt={generation.prompt}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                loading="lazy"
              />
            </button>
            {turn.refined_prompt && (
              <div className="p-2.5 space-y-2">
                <details className="group/details">
                  <summary className="text-[10px] font-semibold uppercase tracking-wider text-text-muted cursor-pointer hover:text-text-secondary list-none flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      className="transition-transform group-open/details:rotate-90"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    Prompt
                  </summary>
                  <p className="mt-1.5 text-[11px] text-text-secondary font-mono leading-relaxed">
                    {turn.refined_prompt}
                  </p>
                </details>
                <button
                  onClick={() => onReuse(turn.refined_prompt!)}
                  className="w-full text-[11px] px-2.5 py-1.5 rounded-lg border border-border hover:border-neon hover:text-neon text-text-secondary transition-colors cursor-pointer"
                >
                  Use in Studio →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
